import React, { useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import { selectTheme } from 'components/CustomerCards/CustomerCardsSearchBar';

export interface MonthOption {
    label: string;
    value: string;
}

interface GhgPeriodPickerProps {
    /** Report period bounds as 'YYYY-MM'. */
    periodStart: string;
    periodEnd: string;
    onPeriodStartChange: (value: string) => void;
    onPeriodEndChange: (value: string) => void;
    onSubmit: () => void;
    loading: boolean;
    error: string | null;
}

/** How far back a report period may start, in months from the current one. */
const SELECTABLE_MONTHS = 36;

/**
 * Selectable months in chronological order, oldest first, ending with the current month — so a period
 * reads left-to-right the way it is chosen, start before end.
 */
export function monthOptions(): MonthOption[] {
    const options: MonthOption[] = [];
    const month = moment().startOf('month').subtract(SELECTABLE_MONTHS - 1, 'month');

    for (let i = 0; i < SELECTABLE_MONTHS; i++) {
        options.push({ value: month.format('YYYY-MM'), label: month.format('MMMM YYYY') });
        month.add(1, 'month');
    }

    return options;
}

export default function GhgPeriodPicker(props: GhgPeriodPickerProps) {
    const options = useMemo(monthOptions, []);
    const findOption = (value: string) => options.find(o => o.value === value) ?? null;

    return (
        <div className="card mb-4">
            <div className="card-body">
                <h3 className="u-fs-18 font-weight-bold mb-3">Raportiperiood</h3>

                <div className="ghg-period-row">
                    <div className="ghg-period-select">
                        <label className="u-fs-14 font-weight-bold">Algus (kuu)</label>
                        <Select
                            options={options}
                            value={findOption(props.periodStart)}
                            onChange={(val) => val && props.onPeriodStartChange((val as MonthOption).value)}
                            isSearchable={false}
                            theme={selectTheme}
                        />
                    </div>
                    <div className="ghg-period-select">
                        <label className="u-fs-14 font-weight-bold">Lõpp (kuu)</label>
                        <Select
                            options={options}
                            value={findOption(props.periodEnd)}
                            onChange={(val) => val && props.onPeriodEndChange((val as MonthOption).value)}
                            isSearchable={false}
                            theme={selectTheme}
                        />
                    </div>
                    <div className="ghg-period-action">
                        <Button
                            variant="primary"
                            className="ghg-submit"
                            onClick={props.onSubmit}
                            disabled={props.loading || !!props.error}
                        >
                            {/* The label stays in the layout while loading so the button keeps its width,
                                with the spinner overlaid on top of it. */}
                            <span className={'px-2' + (props.loading ? ' invisible' : '')}>Koosta raport</span>
                            {props.loading
                                ? <Spinner size="sm" animation="border" className="ghg-submit__spinner" />
                                : null}
                        </Button>
                    </div>
                </div>

                {props.error
                    ? <p className="u-fs-14 text-danger ghg-period-hint">{props.error}</p>
                    : <p className="u-fs-14 ghg-muted ghg-period-hint">
                        Periood valitakse täis-kalendrikuude kaupa, maksimaalselt 12 kuud.
                      </p>}
            </div>
        </div>
    );
}
